import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Bot, Check, X, ShieldCheck, ShieldAlert, Clock, UserPlus, Trash2, Loader2, Users, ArrowRight,
  BarChart2, TrendingUp, Lightbulb,
} from "lucide-react";
import Sparkline from "@/components/Sparkline";
import Heatmap from "@/components/Heatmap";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

function timeAgo(iso) {
  if (!iso) return "";
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export default function BotAccess() {
  const { user, isAdmin } = useAuth();
  const [mine, setMine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [people, setPeople] = useState([]);
  const [showRequest, setShowRequest] = useState(false);
  const [reqTarget, setReqTarget] = useState("");
  const [reqReason, setReqReason] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [teamAnalytics, setTeamAnalytics] = useState(null);
  const [actionRate, setActionRate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissedNudges, setDismissedNudges] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("ragnes_dismissed_nudges") || "[]"));
    } catch { return new Set(); }
  });
  const dismissNudge = async (id, scope = "personal") => {
    // Optimistic hide.
    setDismissedNudges((prev) => {
      const next = new Set(prev); next.add(id);
      localStorage.setItem("ragnes_dismissed_nudges", JSON.stringify(Array.from(next)));
      return next;
    });
    // Persist server-side so it survives sessions and other devices.
    // The team scope prefix `team_` is preserved so the server key routes
    // to `team:<id>` correctly.
    const nudgeId = scope === "team" && id.startsWith("team_") ? id.slice(5) : id;
    try {
      await api.post("/bot-access/nudges/dismiss",
                     { nudge_id: nudgeId, scope });
    } catch { /* non-fatal */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [m, p, a] = await Promise.all([
        api.get("/bot-access/mine"),
        api.get("/memory/people"),
        api.get("/bot-access/analytics/mine", { params: { days: 7 } }).catch(() => ({ data: null })),
      ]);
      setMine(m.data);
      setPeople((p.data || []).filter((x) => x.user_id !== user?.id));
      setAnalytics(a.data);
      if (isAdmin) {
        const [t, ar] = await Promise.all([
          api.get("/bot-access/analytics/workspace", { params: { days: 7 } }).catch(() => ({ data: null })),
          api.get("/bot-access/nudges/action-rate", { params: { days: 7 } }).catch(() => ({ data: null })),
        ]);
        setTeamAnalytics(t.data);
        setActionRate(ar.data);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const request = async () => {
    if (!reqTarget) return toast.error("Pick a teammate");
    setBusy(true);
    try {
      const r = await api.post("/bot-access/request", { target_user_id: reqTarget, reason: reqReason });
      if (r.data.already) toast.info("Request already exists");
      else toast.success("Request sent");
      setShowRequest(false); setReqTarget(""); setReqReason("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const decide = async (grantId, approve) => {
    setBusy(true);
    try {
      await api.post(`/bot-access/${grantId}/decide`, { approve, note: null });
      toast.success(approve ? "Granted" : "Declined");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const revoke = async (grantId) => {
    if (!confirm("Revoke access?")) return;
    setBusy(true);
    try {
      await api.post(`/bot-access/${grantId}/revoke`);
      toast.success("Revoked");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (loading || !mine) {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }

  const Section = ({ title, subtitle, rows, empty, renderRow, tid }) => (
    <div className="border border-border/60 rounded-lg p-5" data-testid={tid}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-medium">{title}</h2>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <span className="text-xs text-muted-foreground font-mono">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">{empty}</p>
      ) : (
        <ul className="divide-y divide-border/40">{rows.map(renderRow)}</ul>
      )}
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="bot-access-page">
      <header className="flex items-center justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-emerald-500">Bot access</p>
          <h1 className="font-display text-3xl mt-1">Who can query whose bot</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Every teammate's personal memory bot is private by default. Request access to query them via <code>/alias</code> in chat.
            Admins can query any bot. You can revoke access at any time.
          </p>
        </div>
        <Button data-testid="bot-access-request-btn" onClick={() => setShowRequest(true)}>
          <UserPlus className="w-4 h-4 mr-2" /> Request access
        </Button>
      </header>

      <div className="grid md:grid-cols-2 gap-4">
        {(() => {
          const allNudges = [
            ...((analytics?.nudges || []).map((n) => ({ ...n, scope: "personal" }))),
            ...(isAdmin ? (teamAnalytics?.nudges || []).map((n) => ({ ...n, scope: "team", id: `team_${n.id}` })) : []),
          ].filter((n) => !dismissedNudges.has(n.id));
          if (allNudges.length === 0) return null;
          return (
            <div className="md:col-span-2 space-y-2" data-testid="bot-insight-nudges">
              {allNudges.map((n) => (
                <div key={n.id}
                     data-testid={`bot-nudge-${n.id}`}
                     className={`rounded-lg border p-4 flex items-start gap-3 ${
                       n.tone === "warn"
                         ? "border-amber-500/40 bg-amber-500/5"
                         : "border-sky-500/40 bg-sky-500/5"
                     }`}>
                  <Lightbulb className={`w-4 h-4 mt-0.5 shrink-0 ${n.tone === "warn" ? "text-amber-500" : "text-sky-500"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium">{n.title}</p>
                      {n.scope === "team" && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-fuchsia-500/15 text-fuchsia-500">team</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{n.body}</p>
                  </div>
                  {n.cta_href && (
                    <Link to={`${n.cta_href}?nudge=${encodeURIComponent(n.id)}&scope=${n.scope}`}
                          className="text-xs font-medium text-sky-500 hover:underline shrink-0"
                          data-testid={`bot-nudge-cta-${n.id}`}>
                      {n.cta_label || "Open"} <ArrowRight className="w-3 h-3 inline" />
                    </Link>
                  )}
                  <button onClick={() => dismissNudge(n.id, n.scope)}
                          className="text-muted-foreground hover:text-foreground shrink-0"
                          data-testid={`bot-nudge-dismiss-${n.id}`}
                          title="Dismiss">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          );
        })()}
        {analytics && (
          <div className="md:col-span-2 border border-border/60 rounded-lg p-5" data-testid="bot-analytics-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <BarChart2 className="w-4 h-4 text-sky-500" />
                <h2 className="text-sm font-medium">Who queried your bot this week</h2>
              </div>
              <div className="flex items-center gap-3">
                {analytics.trend && analytics.trend.length > 1 && (
                  <div className="flex items-center gap-2" data-testid="bot-analytics-sparkline">
                    <TrendingUp className="w-3.5 h-3.5 text-sky-500" />
                    <Sparkline data={analytics.trend} width={140} height={36}
                               stroke="#2563EB" fill="#2563EB22"
                               ariaLabel="Queries per day"
                               testId="bot-analytics-sparkline-svg" />
                  </div>
                )}
                <div className="text-xs text-muted-foreground font-mono">
                  {analytics.total_queries} queries · {analytics.unique_queriers} people · last {analytics.days}d
                </div>
              </div>
            </div>
            {analytics.total_queries === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No one has queried your bot yet. Grant access from People to invite queries.</p>
            ) : (
              <>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="eyebrow mb-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Top queriers</p>
                    <ul className="space-y-2" data-testid="bot-analytics-queriers">
                      {(analytics.queriers || []).slice(0, 8).map((q, i) => (
                        <li key={i} className="flex items-center gap-3 text-sm">
                          <span className="w-6 text-right font-mono text-muted-foreground">{q.count}</span>
                          <div className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center text-[10px] font-semibold">
                            {(q.user?.name || q.user?.email || "?")[0].toUpperCase()}
                          </div>
                          <span className="flex-1 truncate">{q.user?.name || q.user?.email || "(deleted user)"}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="eyebrow mb-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Top topics</p>
                    <div className="flex flex-wrap gap-1.5" data-testid="bot-analytics-topics">
                      {(analytics.top_topics || []).map((t) => (
                        <span key={t.word}
                              className="text-xs px-2 py-1 rounded-full border border-border/60 bg-secondary/30">
                          <span className="font-medium">{t.word}</span>
                          <span className="text-muted-foreground ml-1">×{t.count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                {analytics.heatmap && (
                  <div className="mt-6 pt-4 border-t border-border/40">
                    <p className="eyebrow mb-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">When queries land (UTC)</p>
                    <Heatmap grid={analytics.heatmap}
                             insights={analytics.insights}
                             testId="bot-analytics-heatmap" />
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {isAdmin && actionRate && (actionRate.total_interacted > 0 || true) && (
          <div className="md:col-span-2 border border-border/60 rounded-lg p-4 flex flex-wrap items-center gap-6"
               data-testid="nudge-action-rate-tile">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-fuchsia-500/10 border-2 border-fuchsia-500/40 flex items-center justify-center">
                <span className="text-lg font-semibold text-fuchsia-500 font-mono" data-testid="nudge-action-rate-value">
                  {actionRate.rate}%
                </span>
              </div>
              <div>
                <p className="text-sm font-medium">Nudge action rate — last {actionRate.days}d</p>
                <p className="text-xs text-muted-foreground">
                  {actionRate.total_interacted === 0
                    ? "No nudges touched yet this week — patterns are quiet."
                    : `${actionRate.actioned} actioned · ${actionRate.dismissed} dismissed`}
                </p>
              </div>
            </div>
            <div className="ml-auto text-[11px] text-muted-foreground max-w-xs">
              Higher = your team is turning nudges into memory, docs, or process — not just clearing them.
            </div>
          </div>
        )}
        {isAdmin && teamAnalytics && (
          <div className="md:col-span-2 border border-border/60 rounded-lg p-5" data-testid="team-analytics-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-fuchsia-500" />
                <h2 className="text-sm font-medium">Team-wide bot activity</h2>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-500/15 text-fuchsia-500 uppercase tracking-wider">Admin</span>
              </div>
              <div className="flex items-center gap-3">
                {teamAnalytics.trend && teamAnalytics.trend.length > 1 && (
                  <div className="flex items-center gap-2" data-testid="team-analytics-sparkline">
                    <TrendingUp className="w-3.5 h-3.5 text-fuchsia-500" />
                    <Sparkline data={teamAnalytics.trend} width={140} height={36}
                               stroke="#c026d3" fill="#c026d322"
                               ariaLabel="Team queries per day"
                               testId="team-analytics-sparkline-svg" />
                  </div>
                )}
                <div className="text-xs text-muted-foreground font-mono">
                  {teamAnalytics.total_queries} queries · {teamAnalytics.unique_bots} bots · {teamAnalytics.unique_queriers} people · last {teamAnalytics.days}d
                </div>
              </div>
            </div>
            {teamAnalytics.total_queries === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No <code>/alias</code> queries anywhere in the workspace this week.</p>
            ) : (
              <>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <p className="eyebrow mb-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Most-queried bots</p>
                    <ul className="space-y-2" data-testid="team-analytics-top-bots">
                      {(teamAnalytics.top_bots || []).slice(0, 8).map((q, i) => (
                        <li key={i} className="flex items-center gap-3 text-sm">
                          <span className="w-6 text-right font-mono text-muted-foreground">{q.count}</span>
                          <Bot className="w-3.5 h-3.5 text-fuchsia-500" />
                          <span className="flex-1 truncate">{q.user?.name || q.user?.email || "(deleted)"}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="eyebrow mb-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Top askers</p>
                    <ul className="space-y-2" data-testid="team-analytics-top-queriers">
                      {(teamAnalytics.top_queriers || []).slice(0, 8).map((q, i) => (
                        <li key={i} className="flex items-center gap-3 text-sm">
                          <span className="w-6 text-right font-mono text-muted-foreground">{q.count}</span>
                          <div className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center text-[10px] font-semibold">
                            {(q.user?.name || q.user?.email || "?")[0].toUpperCase()}
                          </div>
                          <span className="flex-1 truncate">{q.user?.name || q.user?.email || "(deleted)"}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="eyebrow mb-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Topics</p>
                    <div className="flex flex-wrap gap-1.5" data-testid="team-analytics-topics">
                      {(teamAnalytics.top_topics || []).map((t) => (
                        <span key={t.word}
                              className="text-xs px-2 py-1 rounded-full border border-border/60 bg-secondary/30">
                          <span className="font-medium">{t.word}</span>
                          <span className="text-muted-foreground ml-1">×{t.count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                {teamAnalytics.heatmap && (
                  <div className="mt-6 pt-4 border-t border-border/40">
                    <p className="eyebrow mb-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">When the team asks (UTC)</p>
                    <Heatmap grid={teamAnalytics.heatmap}
                             baseColor="217, 70, 239"
                             insights={teamAnalytics.insights}
                             testId="team-analytics-heatmap" />
                  </div>
                )}
              </>
            )}
          </div>
        )}
        <Section
          tid="bot-access-pending-incoming"
          title="Requests waiting on you"
          subtitle="Someone wants to query your bot"
          empty="Nothing right now."
          rows={mine.pending_incoming}
          renderRow={(r) => (
            <li key={r.id} className="py-3 flex items-center gap-3" data-testid={`incoming-${r.id}`}>
              <ShieldAlert className="w-4 h-4 text-amber-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm">
                  <span className="font-medium">{r.grantee?.name || r.grantee?.email}</span> wants access
                </p>
                {r.reason && <p className="text-xs text-muted-foreground italic">"{r.reason}"</p>}
                <p className="text-[10px] text-muted-foreground">{timeAgo(r.requested_at)}</p>
              </div>
              <Button size="sm" variant="outline" data-testid={`incoming-approve-${r.id}`}
                      onClick={() => decide(r.id, true)} disabled={busy}>
                <Check className="w-3 h-3 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="ghost" data-testid={`incoming-deny-${r.id}`}
                      onClick={() => decide(r.id, false)} disabled={busy}>
                <X className="w-3 h-3 mr-1" /> Decline
              </Button>
            </li>
          )}
        />
        <Section
          tid="bot-access-pending-outgoing"
          title="Your outgoing requests"
          subtitle="Waiting for approval"
          empty="No pending requests."
          rows={mine.pending_outgoing}
          renderRow={(r) => (
            <li key={r.id} className="py-3 flex items-center gap-3" data-testid={`outgoing-${r.id}`}>
              <Clock className="w-4 h-4 text-muted-foreground" />
              <div className="flex-1">
                <p className="text-sm">Waiting on <span className="font-medium">{r.target?.name || r.target?.email}</span></p>
                <p className="text-[10px] text-muted-foreground">{timeAgo(r.requested_at)}</p>
              </div>
              <Button size="sm" variant="ghost" data-testid={`outgoing-cancel-${r.id}`}
                      onClick={() => revoke(r.id)} disabled={busy}>
                <Trash2 className="w-3 h-3 mr-1" /> Cancel
              </Button>
            </li>
          )}
        />
        <Section
          tid="bot-access-held"
          title="Bots you can query"
          subtitle="Use these aliases with /alias in chat"
          empty={isAdmin ? "As an admin you can query any bot with /alias." : "You don't have access to any teammate's bot yet."}
          rows={mine.held}
          renderRow={(r) => (
            <li key={r.id} className="py-3 flex items-center gap-3" data-testid={`held-${r.id}`}>
              <Bot className="w-4 h-4 text-emerald-500" />
              <div className="flex-1">
                <p className="text-sm font-medium">{r.target?.name || r.target?.email}</p>
                <p className="text-[10px] text-muted-foreground">Granted {timeAgo(r.decided_at)}</p>
              </div>
              <Link
                to="/app/chat"
                className="text-xs text-sky-500 hover:underline"
                data-testid={`held-query-${r.id}`}
              >
                Ask AI <ArrowRight className="w-3 h-3 inline" />
              </Link>
              <Button size="sm" variant="ghost" data-testid={`held-revoke-${r.id}`}
                      onClick={() => revoke(r.id)} disabled={busy}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </li>
          )}
        />
        <Section
          tid="bot-access-given"
          title="Access you've granted"
          subtitle="Colleagues who can query your bot"
          empty="Nobody has access to your bot."
          rows={mine.given}
          renderRow={(r) => (
            <li key={r.id} className="py-3 flex items-center gap-3" data-testid={`given-${r.id}`}>
              <ShieldCheck className="w-4 h-4 text-sky-500" />
              <div className="flex-1">
                <p className="text-sm font-medium">{r.grantee?.name || r.grantee?.email}</p>
                <p className="text-[10px] text-muted-foreground">Granted {timeAgo(r.decided_at)}</p>
              </div>
              <Button size="sm" variant="ghost" data-testid={`given-revoke-${r.id}`}
                      onClick={() => revoke(r.id)} disabled={busy}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </li>
          )}
        />
      </div>

      <Dialog open={showRequest} onOpenChange={setShowRequest}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request access to a bot</DialogTitle>
            <DialogDescription>
              The target user will get a notification and can approve or decline.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Teammate</label>
              <select
                data-testid="bot-access-target-select"
                value={reqTarget}
                onChange={(e) => setReqTarget(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded border border-border/60 bg-background text-sm"
              >
                <option value="">Pick a teammate…</option>
                {people.map((p) => (
                  <option key={p.user_id} value={p.user_id}>
                    {p.name || p.email}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Reason (optional)</label>
              <Textarea
                data-testid="bot-access-reason"
                value={reqReason}
                onChange={(e) => setReqReason(e.target.value)}
                rows={3}
                placeholder="Why do you need to query this bot?"
                className="mt-1 bg-background"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowRequest(false)}>Cancel</Button>
              <Button data-testid="bot-access-send-request" onClick={request} disabled={busy}>
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Send request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
